import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CartItem, PaymentEntry } from '@/types';
import { calcLineAmount, roundMoney } from '@/utils/currency';

export const MAX_HELD_CARTS = 5;

export interface CartSnapshot {
  id: string;
  label: string;
  heldAt: string;
  items: CartItem[];
  customer: string;
  customerName: string;
  customerType: string;
  discount: number;
  tax: number;
  notes: string;
  payments: PaymentEntry[];
  customerMobile?: string;
  editingInvoiceDate?: string;
}

export type HoldCartResult = { ok: true; id: string } | { ok: false; error: string };
export type ResumeHoldResult = { ok: true } | { ok: false; error: string };



interface CartStore {
  items: CartItem[];
  customer: string;
  customerName: string;
  customerType: string;
  discount: number;
  tax: number;
  notes: string;
  editingInvoiceDate?: string;
  payments: PaymentEntry[];
  holds: CartSnapshot[];
  repeatItems: boolean;

  toggleRepeatItems: () => void;
  addItem: (item: CartItem) => void;
  removeItem: (index: number) => void;
  updateItemName: (index: number, newName: string) => void;
  updateGeneralItem: (index: number, newName: string, costPrice: number, salePrice: number, isService: boolean) => void;
  updateQuantity: (index: number, qty: number) => void;
  updateRate: (index: number, rate: number) => void;
  updateSerial: (index: number, serial: string, imei?: string, mac?: string) => void;
  setCustomer: (id: string, name: string, type?: string) => void;
  setDiscount: (d: number) => void;
  setTax: (t: number) => void;
  setNotes: (n: string) => void;
  setEditingInvoiceDate: (d?: string) => void;
  addPayment: (payment: PaymentEntry) => void;
  removePayment: (index: number) => void;
  updatePayment: (index: number, payment: Partial<PaymentEntry>) => void;
  clearCart: () => void;

  holdCart: (opts?: { customerMobile?: string }) => HoldCartResult;
  resumeHold: (id: string, opts?: { customerMobile?: string }) => ResumeHoldResult;
  discardHold: (id: string) => void;

  getSubtotal: () => number;
  getItemTotal: () => number;
  getServiceTotal: () => number;
  getDiscountAmount: () => number;
  getTaxAmount: () => number;
  getNetTotal: () => number;
  getPaidAmount: () => number;
  getBalance: () => number;
  getTotalItems: () => number;
}

function snapshotFromState(
  s: {
    items: CartItem[];
    customer: string;
    customerName: string;
    customerType: string;
    discount: number;
    tax: number;
    notes: string;
    payments: PaymentEntry[];
    holds: CartSnapshot[];
    editingInvoiceDate?: string;
  },
  customerMobile?: string,
): CartSnapshot {
  const id = `hold_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const label =
    s.customerName?.trim() ||
    (s.customer ? s.customer : `Hold #${s.holds.length + 1}`);
  return {
    id,
    label,
    heldAt: new Date().toISOString(),
    items: s.items.map((i) => ({ ...i })),
    customer: s.customer,
    customerName: s.customerName,
    customerType: s.customerType || 'Retailer',
    discount: s.discount,
    tax: s.tax,
    notes: s.notes,
    payments: s.payments.map((p) => ({ ...p })),
    customerMobile: customerMobile || undefined,
    editingInvoiceDate: s.editingInvoiceDate,
  };
}

const emptyActive = {
  items: [] as CartItem[],
  customer: '',
  customerName: '',
  customerType: 'Retailer',
  discount: 0,
  tax: 0,
  notes: '',
  editingInvoiceDate: undefined,
  payments: [] as PaymentEntry[],
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      ...emptyActive,
      holds: [],
      repeatItems: false,

      toggleRepeatItems: () => set((state) => ({ repeatItems: !state.repeatItems })),

      addItem: (item) => {
        const items = get().items;
        // Serial/IMEI lines must stay separate (one unit per IMEI)
        const isSerialLine = !!(item.imei || item.serial || item.track_serials);
        const existing = (isSerialLine || get().repeatItems)
          ? -1
          : items.findIndex(
              (i) =>
                i.item === item.item &&
                i.item_name === item.item_name &&
                i.variant === item.variant &&
                !i.imei &&
                !i.serial
            );
        if (existing >= 0) {
          const updated = [...items];
          updated[existing].quantity += item.quantity;
          updated[existing].amount = calcLineAmount(updated[existing].quantity, updated[existing].rate);
          if (item.min_rate != null) {
            updated[existing].min_rate = item.min_rate;
          }
          if (item.tax_rate != null) {
            updated[existing].tax_rate = item.tax_rate;
          }
          set({ items: updated });
        } else {
          set({
            items: [...items, {
              ...item,
              amount: calcLineAmount(item.quantity, item.rate),
            }],
          });
        }
      },

      removeItem: (index) => set({ items: get().items.filter((_, i) => i !== index) }),

      updateItemName: (index, newName) => {
        const items = [...get().items];
        items[index].item_name = newName;
        set({ items });
      },

      updateGeneralItem: (index, newName, costPrice, salePrice, isService) => {
        const items = [...get().items];
        items[index].item_name = newName;
        items[index].cost_price = costPrice;
        items[index].rate = salePrice;
        items[index].amount = calcLineAmount(items[index].quantity, salePrice);
        items[index].item_type = isService ? 'Service' : 'Product';
        set({ items });
      },

      updateQuantity: (index, qty) => {
        if (qty < 0) return;
        const items = [...get().items];
        items[index].quantity = qty;
        items[index].amount = calcLineAmount(qty, items[index].rate);
        set({ items });
      },

      updateRate: (index, rate) => {
        set((state) => {
          const items = [...state.items];
          items[index].rate = rate;
          items[index].amount = rate * items[index].quantity;
          return { items };
        });
      },

      updateSerial: (index, serial, imei, mac) => {
        set((state) => {
          const items = [...state.items];
          items[index].serial = serial;
          if (imei) items[index].imei = imei;
          if (mac) items[index].mac_address = mac;
          return { items };
        });
      },

      setCustomer: (id, name, type = 'Customer') => {
        const prev = get();
        // Avoid noop updates — remapping items always created a new array and could
        // retrigger POS effects into "Maximum update depth exceeded".
        if (prev.customer === id && prev.customerName === name && prev.customerType === type) {
          return;
        }

        let items = prev.items;

        // Only update prices if the customer type actually changed
        if (prev.customerType !== type) {
          items = prev.items.map(item => {
            let newRate = item.retail_price ?? item.rate;
            if (type === 'Wholesale') {
              if (item.wholesale_price != null && item.wholesale_price > 0) {
                newRate = item.wholesale_price;
              } else if (item.reseller_price != null && item.reseller_price > 0) {
                newRate = item.reseller_price;
              }
            } else if (type === 'Distributor') {
              if (item.reseller_price != null && item.reseller_price > 0) {
                newRate = item.reseller_price;
              }
            }
            return {
              ...item,
              rate: newRate,
              amount: calcLineAmount(item.quantity, newRate),
            };
          });
        }
        
        set({ customer: id, customerName: name, customerType: type, items });
      },
      setDiscount: (d) => set({ discount: d }),
      setTax: (t) => set({ tax: t }),
      setNotes: (n) => set({ notes: n }),
      setEditingInvoiceDate: (d) => set({ editingInvoiceDate: d }),

      addPayment: (payment) => set({ payments: [...get().payments, payment] }),
      removePayment: (index) => set({ payments: get().payments.filter((_, i) => i !== index) }),
      updatePayment: (index, payment) => {
        const payments = [...get().payments];
        payments[index] = { ...payments[index], ...payment };
        set({ payments });
      },

      clearCart: () => set({ ...emptyActive }),


      holdCart: (opts) => {
        const s = get();
        if (!s.items.length) {
          return { ok: false, error: 'Cart is empty — nothing to hold' };
        }
        if (s.holds.length >= MAX_HELD_CARTS) {
          return {
            ok: false,
            error: `Maximum ${MAX_HELD_CARTS} held carts. Resume or discard one first.`,
          };
        }
        const snap = snapshotFromState(s, opts?.customerMobile);
        set({ holds: [...s.holds, snap], ...emptyActive });
        return { ok: true, id: snap.id };
      },

      resumeHold: (id, opts) => {
        const s = get();
        const idx = s.holds.findIndex((h) => h.id === id);
        if (idx < 0) return { ok: false, error: 'Held cart not found' };

        let nextHolds = [...s.holds];
        const target = nextHolds[idx];

        // If active cart has lines, park it first when there is room.
        if (s.items.length > 0) {
          if (nextHolds.length >= MAX_HELD_CARTS) {
            return {
              ok: false,
              error: `Active cart has items and ${MAX_HELD_CARTS} holds are full. Complete, clear, or discard a hold first.`,
            };
          }
          const parked = snapshotFromState(s, opts?.customerMobile);
          nextHolds = [...nextHolds, parked];
        }

        nextHolds = nextHolds.filter((h) => h.id !== id);
        set({
          holds: nextHolds,
          items: target.items.map((i) => ({ ...i })),
          customer: target.customer,
          customerName: target.customerName,
          customerType: target.customerType || 'Retailer',
          discount: target.discount,
          tax: target.tax,
          notes: target.notes,
          editingInvoiceDate: target.editingInvoiceDate,
          payments: target.payments.map((p) => ({ ...p })),
        });
        return { ok: true };
      },

      discardHold: (id) => {
        set({ holds: get().holds.filter((h) => h.id !== id) });
      },

      getSubtotal: () => roundMoney(get().items.reduce((s, i) => s + i.amount, 0)),
      getItemTotal: () => roundMoney(get().items.filter(i => i.item_type === 'Product').reduce((s, i) => s + i.amount, 0)),
      getServiceTotal: () => roundMoney(get().items.filter(i => i.item_type === 'Service').reduce((s, i) => s + i.amount, 0)),
      getDiscountAmount: () => roundMoney(get().discount),
      getTaxAmount: () => roundMoney(get().tax),
      getNetTotal: () => {
        const s = get().getSubtotal();
        return roundMoney(s - get().discount + get().tax);
      },
      getPaidAmount: () => roundMoney(get().payments.reduce((s, p) => s + (p.amount || 0), 0)),
      getBalance: () => roundMoney(get().getNetTotal() - get().getPaidAmount()),
      getTotalItems: () => get().items.reduce((s, i) => s + i.quantity, 0),
    }),
    {
      name: 'pos-cart-storage',
      storage: createJSONStorage(() => sessionStorage),
      merge: (persisted, current) => {
        const p = (persisted || {}) as Partial<CartStore>;
        return {
          ...current,
          ...p,
          holds: Array.isArray(p.holds) ? p.holds : [],
          items: Array.isArray(p.items) ? p.items : [],
          payments: Array.isArray(p.payments) ? p.payments : [],
        };
      },
    }
  )
);
